import { useState } from 'react'
import { leases as leaseApi, review as reviewApi, signups as signupApi } from '@/api/endpoints'
import type { ReviewItem } from '@/api/types'
import { Err } from '@/components/Err'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { KIND_TITLE, type BatchAction, batchActions, rejectNeedsReason, reviewKey } from '@/domain/review'
import { CHIP_BRAND, CHIP_DANGER, CHIP_PLAIN } from './chips'

// 검토 대기 제목 줄의 일괄 조작 — 대상은 체크한 항목들이다. 칩은 고른 항목 모두의 `actions` 가 허락할 때만 선다.
// 한 건씩 차례로 부르고, 끝난 것은 줄이 사라지며(onDone) 실패한 것만 골라진 채 남는다 — 같은 칩이 곧 재시도다.
// 승인은 신청한 그대로다(본문 없음). 숫자를 고쳐 승인하는 창은 줄의 `승인` 에만 있다.
// 도는 동안(busy)은 부르는 쪽이 고르기와 대상 줄의 칩을 잠근다 — 누른 순간의 목록을 끝까지 돌기 때문이다.

/** 일괄 승인·무시의 첫 오류와 실패한 항목의 열쇠. 실패한 것이 하나도 골라져 있지 않으면 부르는 쪽이 거둔다. */
export type BatchFailure = { error: unknown; keys: ReadonlySet<string> }

function call(item: ReviewItem, action: BatchAction, reason: string): Promise<unknown> {
  const id = item.subject_id
  const change = item.detail.change
  if (action === 'dismiss') return reviewApi.dismiss(item)
  if (item.kind === 'signup_request') return action === 'approve' ? signupApi.approve(id) : signupApi.reject(id)
  if (item.kind === 'lease_change' && change) {
    return action === 'approve' ? leaseApi.approveChange(id, change.id) : leaseApi.rejectChange(id, change.id, reason)
  }
  // GPU 승인 — 나머지 종류는 `batchable` 이 고르지 못하게 막고, `run` 이 그 조작을 허락하지 않는 항목을 부르지 않는다.
  return action === 'approve' ? leaseApi.approve(id) : leaseApi.reject(id, reason)
}

export function BatchActions({
  chosen,
  busy,
  onBusy,
  onDone,
  onFailure,
}: {
  chosen: ReviewItem[]
  busy: BatchAction | null
  onBusy: (action: BatchAction | null) => void
  onDone: (item: ReviewItem) => void
  onFailure: (failure: BatchFailure | null) => void
}) {
  // 반려 창이 열린 순간의 대상 — 창은 그 목록을 확인받은 것이라 그 밖의 항목에는 반려가 가지 않는다.
  const [rejecting, setRejecting] = useState<ReadonlySet<string> | null>(null)
  const [reason, setReason] = useState('')
  const [error, setError] = useState<unknown>(null)
  const allowed = batchActions(chosen)
  const targets = rejecting === null ? [] : chosen.filter((item) => rejecting.has(reviewKey(item)))
  const open = targets.length > 0 && batchActions(targets).includes('reject')
  const needsReason = rejectNeedsReason(targets)
  // 창을 연 사이 대상이 모두 사라졌으면(다른 관리자가 먼저 결정했다) 창과 사유를 거둔다 —
  // 남겨 두면 다음에 무엇을 고르든 그 위에 창이 사유가 채워진 채 저절로 다시 선다.
  if (rejecting !== null && !open && busy === null) {
    setRejecting(null)
    setReason('')
    setError(null)
  }

  // 첫 오류만 말한다 — 승인·무시는 제목 줄 아래에서, 반려는 그 창 안에서.
  // 누른 순간의 목록이 그 조작을 모두 허락하지 않으면 아무것도 부르지 않는다.
  const run = async (action: BatchAction, list: ReviewItem[]) => {
    if (list.length === 0 || !batchActions(list).includes(action)) return false
    onBusy(action)
    onFailure(null)
    setError(null)
    let first: unknown = null
    const failed = new Set<string>()
    for (const item of list) {
      try {
        await call(item, action, reason.trim())
        onDone(item)
      } catch (err) {
        if (first === null) first = err
        failed.add(reviewKey(item))
      }
    }
    onBusy(null)
    if (first !== null) {
      if (action === 'reject') setError(first)
      else onFailure({ error: first, keys: failed })
    }
    return first === null
  }
  const reject = async () => {
    if (!open || (needsReason && !reason.trim())) return
    if (await run('reject', targets)) {
      setRejecting(null)
      setReason('')
    }
  }

  return (
    <>
      {allowed.length > 0 ? (
        <div role="group" aria-label="일괄 처리" className="mt-0.5 flex flex-wrap items-center justify-end gap-1.5">
          {allowed.includes('approve') ? (
            <button type="button" className={CHIP_BRAND} disabled={busy !== null} onClick={() => void run('approve', chosen)}>
              {busy === 'approve' ? '승인 중' : '승인'}
            </button>
          ) : null}
          {allowed.includes('reject') ? (
            <button
              type="button"
              className={CHIP_DANGER}
              disabled={busy !== null}
              onClick={() => {
                onFailure(null)
                setError(null)
                setReason('')
                setRejecting(new Set(chosen.map(reviewKey)))
              }}
            >
              반려
            </button>
          ) : null}
          {allowed.includes('dismiss') ? (
            <button type="button" className={CHIP_PLAIN} disabled={busy !== null} onClick={() => void run('dismiss', chosen)}>
              {busy === 'dismiss' ? '무시 중' : '무시'}
            </button>
          ) : null}
        </div>
      ) : null}
      <Dialog open={open} onOpenChange={(next) => { if (!next && busy === null) setRejecting(null) }}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-sm">
          <DialogHeader><DialogTitle>{targets.length}건 반려</DialogTitle></DialogHeader>
          <ul aria-label="반려할 항목" className="max-h-60 overflow-y-auto rounded-md border">
            {targets.map((item) => (
              <li key={reviewKey(item)} className="flex items-center gap-2 px-3 py-1.5 text-[13px]">
                <span className="truncate font-medium">{item.subject_name}</span>
                <span className="ml-auto shrink-0 text-muted-foreground">{KIND_TITLE[item.kind]}</span>
              </li>
            ))}
          </ul>
          {/* 가입 신청만 고르면 사유 칸이 없다 — 가입 반려는 사유를 싣지 않는다. */}
          {needsReason ? (
            <div className="grid gap-1.5">
              <Label htmlFor="review-batch-reject-reason">사유</Label>
              <Input id="review-batch-reject-reason" value={reason} autoComplete="off" maxLength={200} disabled={busy !== null} onChange={(e) => setReason(e.target.value)} />
            </div>
          ) : null}
          <Err error={error} />
          <DialogFooter>
            <Button variant="outline" disabled={busy !== null} onClick={() => setRejecting(null)}>취소</Button>
            <Button variant="destructive" disabled={busy !== null || (needsReason && !reason.trim())} onClick={() => void reject()}>
              {busy === 'reject' ? '반려 중' : '반려'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
